import { useState, useRef, useEffect, useCallback } from 'react';
import { TiptapEditorRef } from '../../../../interfaces/tiptapInterface';
import { isEmpty, removeFileFromFirebase } from '../../../../helpers/common';
import { dispatchProjectError } from '../../../../services/projectServices';
import { useMobileDevice } from '../../../../global/useMobile';
import { rootStore } from '../../../../mobx/rootStore';
import { ProjectModel } from '../../../../mobx/models/project';

export function useCreateTaskForm(onCancel: () => void) {
  const { taskStore } = rootStore;
  const { createTaskInput } = taskStore;
  const { project } = createTaskInput;
  const mobile = useMobileDevice();

  // state variables
  const [loading, setLoading] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const [isEstimateDropdownOpen, setIsEstimateDropdownOpen] = useState(false);
  const [isAssignDropdownOpen, setIsAssignDropdownOpen] = useState(false);
  const [closeModel, setCloseModel] = useState(false);
  const [attachment, setAttachment] = useState(false);
  const [selectFile, setSelectFile] = useState(false);
  const [isSnapModalOpen, setIsSnapModalOpen] = useState(false);
  // Version counters to force re-keying dropdowns when MobX values change
  const [labelsVersion, setLabelsVersion] = useState(0);
  const [priorityVersion, setPriorityVersion] = useState(0);

  // Refs variables
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inlineRef = useRef<TiptapEditorRef>(null);
  const titleTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Effects
  // Bump version counters when MobX changes (forces dropdown key change)
  useEffect(() => {
    setLabelsVersion((prev) => prev + 1);
  }, [createTaskInput?.labelsIds]);

  useEffect(() => {
    setPriorityVersion((prev) => prev + 1);
  }, [createTaskInput?.priority]);

  // Auto-resize title textarea
  const resizeTitleTextarea = useCallback(() => {
    const textarea = titleTextareaRef.current;
    if (!textarea) return;
    textarea.style.height = '25px';
    const scrollHeight = textarea.scrollHeight;
    if (scrollHeight > 25) {
      textarea.style.height = `${scrollHeight}px`;
    }
  }, []);

  useEffect(() => {
    resizeTitleTextarea();
  }, [createTaskInput?.name, resizeTitleTextarea]);

  // Set initial height on mount
  useEffect(() => {
    resizeTitleTextarea();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Confirm-close handlers
  const clearData = useCallback(() => {
    if (!isEmpty(createTaskInput?.name) || !isEmpty(createTaskInput?.description) || !isEmpty(createTaskInput.files)) {
      setCloseModel(true);
    } else {
      taskStore.createTaskInput.clear();
      onCancel();
    }
  }, [createTaskInput?.name, createTaskInput?.description, createTaskInput.files, taskStore.createTaskInput, onCancel]);

  const closeConfirmModal = useCallback(() => {
    setCloseModel(false);
    taskStore.createTaskInput.setLabels([]);
  }, [taskStore.createTaskInput]);

  const confirmDiscard = useCallback(() => {
    setCloseModel(false);
    // Delete only the files uploaded to this task from Firebase, keep the selected project files.
    taskStore.createTaskInput.files?.forEach((file) => {
      if (file && !file.uploadFileType && file.href?.startsWith('https://firebasestorage.googleapis'))
        removeFileFromFirebase(file.href);
    });
    taskStore.createTaskInput.clear();
    onCancel();
  }, [onCancel, taskStore.createTaskInput]);

  const closeAttachmentDrawer = useCallback(() => setAttachment(false), []);
  
  const handleOpenSnapModal = useCallback(() => setIsSnapModalOpen(true), []);
  const handleCloseSnapModal = useCallback(() => setIsSnapModalOpen(false), []);

  const handleFileInput = useCallback(() => {
    queueMicrotask(() => {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    });
  }, []);

  const openAttachmentPage = useCallback(() => {
    if (!project?.name) {
      dispatchProjectError('Please select the project');
      return;
    }
    if (project instanceof ProjectModel) project.setCurrentFolderId(null);
    if (mobile) {
      setSelectFile(true);
    } else {
      setAttachment(true);
    }
  }, [project, mobile]);

  return {
    // State
    loading,
    setLoading,
    fileUploading,
    setFileUploading,
    isEstimateDropdownOpen,
    setIsEstimateDropdownOpen,
    isAssignDropdownOpen,
    setIsAssignDropdownOpen,
    closeModel,
    attachment,
    selectFile,
    setSelectFile,
    isSnapModalOpen,
    labelsVersion,
    priorityVersion,
    // Refs
    fileInputRef,
    inlineRef,
    titleTextareaRef,
    // Handlers
    clearData,
    closeConfirmModal,
    confirmDiscard,
    closeAttachmentDrawer,
    handleOpenSnapModal,
    handleCloseSnapModal,
    handleFileInput,
    openAttachmentPage,
    resizeTitleTextarea
  };
}
